import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { Card, CardTitle } from '@/components/ui';
import {
  isBackEnabled,
  isCountingCell,
  type BulkStatusCounts,
} from '@/lib/bulk/pairing';
import { cn } from '@/lib/utils';
import type {
  BulkReferenceProduct,
  BulkReferenceSelection,
  BulkRow,
} from '@/stores/bulkCreationStore';
import { formatPlacementSummary } from './ReferenceCard';

export interface BulkSummaryProps {
  reference: BulkReferenceSelection | null;
  activeProducts: readonly BulkReferenceProduct[];
  rows: readonly BulkRow[];
  counts: BulkStatusCounts;
  className?: string;
}

function SummaryItem({
  label,
  children,
  testId,
}: {
  label: string;
  children: ReactNode;
  testId: string;
}) {
  return (
    <div className="flex flex-col gap-0.5 border-t border-line-2 py-2.5 first:border-t-0 first:pt-0">
      <dt className="text-[12.5px] text-mute">{label}</dt>
      <dd className="text-sm text-ink" data-testid={testId}>
        {children}
      </dd>
    </div>
  );
}

/**
 * Left column of step 03 (R27, R28): what the run will create, taken from
 * the reference card and the rows, plus warnings for designs with one side
 * and for active products that drop an uploaded back.
 */
export function BulkSummary({
  reference,
  activeProducts,
  rows,
  counts,
  className,
}: BulkSummaryProps) {
  const { t } = useTranslation();
  const backEnabled = isBackEnabled(activeProducts);

  const withBack = backEnabled ? counts.backCount : 0;
  const backOnlyDesigns = rows.filter(
    (row) =>
      !isCountingCell(row.front, 'front', backEnabled) &&
      isCountingCell(row.back, 'back', backEnabled)
  ).length;
  const productsWithBack = activeProducts.filter(
    (product) => product.hasBack
  ).length;
  const productsIgnoringBack = activeProducts.length - productsWithBack;

  const methodNames = reference?.methodNames?.join(', ') ?? '';
  const placementText = formatPlacementSummary(t, reference?.placement);

  const showIncompletePairs =
    counts.backCount > 0 && counts.incompletePairs > 0;
  const showIgnoredBack = counts.backCount > 0 && productsIgnoringBack > 0;

  return (
    <Card className={cn('flex flex-col gap-4', className)}>
      <CardTitle>{t('seller.bulk.create.summary.title')}</CardTitle>

      <dl className="flex flex-col">
        <SummaryItem
          label={t('seller.bulk.create.summary.totalProducts')}
          testId="bulk-summary-total"
        >
          <span className="dc-h2 text-2xl">{counts.totalProducts}</span>
        </SummaryItem>
        {reference && (
          <SummaryItem
            label={t('seller.bulk.create.summary.reference')}
            testId="bulk-summary-reference"
          >
            {reference.name}
          </SummaryItem>
        )}
        {methodNames && (
          <SummaryItem
            label={t('seller.bulk.create.summary.method')}
            testId="bulk-summary-method"
          >
            {methodNames}
          </SummaryItem>
        )}
        <SummaryItem
          label={t('seller.bulk.create.summary.designs')}
          testId="bulk-summary-designs"
        >
          {t('seller.bulk.create.summary.designsValue', {
            count: counts.designCount,
            withBack,
          })}
        </SummaryItem>
        <SummaryItem
          label={t('seller.bulk.create.summary.productsPerDesign')}
          testId="bulk-summary-products-per-design"
        >
          <span className="font-mono">{activeProducts.length}</span>{' '}
          {backOnlyDesigns > 0 && (
            <span className="ml-2 text-ink-2">
              {t('seller.bulk.create.summary.productsPerDesignBackOnly', {
                products: productsWithBack,
              })}
            </span>
          )}
        </SummaryItem>
        {placementText && (
          <SummaryItem
            label={t('seller.bulk.create.summary.placement')}
            testId="bulk-summary-placement"
          >
            {placementText}
          </SummaryItem>
        )}
      </dl>

      {(showIncompletePairs || showIgnoredBack) && (
        <section
          aria-label={t('seller.bulk.create.warnings.title')}
          data-testid="bulk-summary-warnings"
          className="flex flex-col gap-2 rounded-control bg-paper px-3.5 py-3 ring-1 ring-inset ring-warning"
        >
          {showIncompletePairs && (
            <p className="flex items-start gap-2 text-sm text-ink">
              <AlertTriangle
                className="mt-0.5 h-4 w-4 shrink-0 text-warning"
                aria-hidden="true"
              />
              {t('seller.bulk.create.warnings.incompletePairs', {
                count: counts.incompletePairs,
              })}
            </p>
          )}
          {showIgnoredBack && (
            <p className="flex items-start gap-2 text-sm text-ink">
              <AlertTriangle
                className="mt-0.5 h-4 w-4 shrink-0 text-warning"
                aria-hidden="true"
              />
              {t('seller.bulk.create.warnings.productsIgnoreBack', {
                count: productsIgnoringBack,
              })}
            </p>
          )}
        </section>
      )}
    </Card>
  );
}
